import React, { useContext, useEffect, useMemo, useState } from "react";
import { Navigate, NavLink, Route, Routes, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
	faBars,
	faCog,
	faCubes,
	faHome,
	faLeaf,
	faPowerOff,
	faXmark,
} from "@fortawesome/free-solid-svg-icons";
import AuthContext from "../../context/AuthProvider";
import packageJson from "../../../package.json";
import logo from "../../images/logo.png";
import Home from "../home/Home";
import DevicesPage from "../home/DevicesPage";
import Aton from "../devices/Aton";
import BTicino from "../devices/BTicino";
import Husqvarna from "../devices/Husqvarna";
import Netatmo from "../devices/Netatmo";
import Shelly from "../devices/Shelly";
import Voltaware from "../devices/Voltaware";
import WasherDryer from "../devices/WasherDryer";
import Zucchetti from "../devices/Zucchetti";
import RuleEngine from "../settings/RuleEngine";
import Settings from "../settings/Settings";

const deviceLinks = [
	{ to: "/devices/netatmo", label: "Netatmo" },
	{ to: "/devices/voltaware", label: "Voltaware" },
	{ to: "/devices/aton", label: "Aton" },
	{ to: "/devices/bticino", label: "BTicino" },
	{ to: "/devices/zucchetti", label: "Zucchetti" },
	{ to: "/devices/smartplug", label: "Shelly Smart Plug" },
	{ to: "/devices/washerdryer", label: "Washer-dryer" },
	{ to: "/devices/husqvarna", label: "Smart Garden" },
];

const formatUsername = (username = "") => {
	const cleanUsername = username.replaceAll('"', "").trim();
	if (!cleanUsername) return "User";

	return cleanUsername
		.split(/[._-]/)
		.filter(Boolean)
		.map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
		.join(" ");
};

const BasePage = ({ user }) => {
	const { signOut } = useContext(AuthContext);
	const location = useLocation();
	const [isSidebarOpen, setIsSidebarOpen] = useState(false);
	const isDevicesOverview = location.pathname === "/home/devices";
	const isDevicesSection =
		location.pathname === "/home/devices" || location.pathname.startsWith("/devices");
	const [isDevicesMenuOpen, setIsDevicesMenuOpen] = useState(isDevicesSection);

	useEffect(() => {
		setIsSidebarOpen(false);
	}, [location.pathname]);

	const displayName = useMemo(() => formatUsername(user), [user]);
	const initials = useMemo(
		() =>
			displayName
				.split(" ")
				.map((part) => part[0])
				.join("")
				.slice(0, 2),
		[displayName]
	);
	const currentDevice = deviceLinks.find(({ to }) => to === location.pathname);
	const breadcrumbs = (() => {
		if (location.pathname === "/home/index") {
			return [{ label: "Home" }];
		}

		if (location.pathname === "/home/devices") {
			return [{ label: "Devices" }];
		}

		if (currentDevice) {
			return [
				{ label: "Devices", to: "/home/devices" },
				{ label: currentDevice.label },
			];
		}

		if (location.pathname === "/settings/ruleengine") {
			return [{ label: "Smart rules" }];
		}

		if (location.pathname === "/settings") {
			return [{ label: "Settings" }];
		}

		return [];
	})();

	const navClassName = ({ isActive }) =>
		`app-nav__link${isActive ? " app-nav__link--active" : ""}`;

	return (
		<div className={`app-shell${isSidebarOpen ? " app-shell--menu-open" : ""}`}>
			<button
				className="app-sidebar-overlay"
				type="button"
				aria-label="Close navigation"
				onClick={() => setIsSidebarOpen(false)}
			/>

			<aside className="app-sidebar" aria-label="Primary navigation">
				<div className="app-sidebar__brand">
					<img src={logo} alt="Edison" />
					<span>DOMUS IoT</span>
					<button
						type="button"
						className="app-sidebar__close"
						onClick={() => setIsSidebarOpen(false)}
						aria-label="Close navigation"
					>
						<FontAwesomeIcon icon={faXmark} />
					</button>
				</div>

				<nav className="app-nav">
					<p className="app-nav__section">OVERVIEW</p>
					<NavLink className={navClassName} to="/home/index">
						<FontAwesomeIcon icon={faHome} />
						<span>Home</span>
					</NavLink>

					<NavLink
						className={() => `app-nav__link${isDevicesOverview ? " app-nav__link--active" : ""}`}
						to="/home/devices"
						onClick={() => setIsDevicesMenuOpen(true)}
					>
						<FontAwesomeIcon icon={faCubes} />
						<span>Devices</span>
					</NavLink>

					{isDevicesMenuOpen && (
						<div className="app-nav__subnav">
							{deviceLinks.map(({ label, to }) => (
								<NavLink className={navClassName} to={to} key={to}>{label}</NavLink>
							))}
						</div>
					)}

					<p className="app-nav__section">MANAGEMENT</p>
					<NavLink className={navClassName} to="/settings/ruleengine">
						<FontAwesomeIcon icon={faLeaf} />
						<span>Smart rules</span>
					</NavLink>
					<NavLink className={navClassName} to="/settings" end>
						<FontAwesomeIcon icon={faCog} />
						<span>Settings</span>
					</NavLink>
				</nav>

				<div className="app-sidebar__footer">
					<div className="app-sidebar-user">
						<span className="app-profile__avatar" aria-hidden="true"><span>{initials}</span></span>
						<strong title={displayName}>{displayName}</strong>
						<button type="button" onClick={signOut} title="Sign out" aria-label="Sign out">
							<FontAwesomeIcon icon={faPowerOff} />
						</button>
					</div>
					<small>Domus IoT Platform - v{packageJson.version}</small>
				</div>
			</aside>

			<div className="app-workspace">
				<header className="app-header">
					<button
						className="app-header__menu"
						type="button"
						onClick={() => setIsSidebarOpen(true)}
						aria-label="Open navigation"
					>
						<FontAwesomeIcon icon={faBars} />
					</button>
					{breadcrumbs.length > 0 && (
						<nav className="app-header__breadcrumbs" aria-label="Breadcrumb">
							{breadcrumbs.map((item, index) => (
								<React.Fragment key={item.to || item.label}>
									{index > 0 && <span aria-hidden="true">/</span>}
									{item.to ? (
										<NavLink to={item.to}>{item.label}</NavLink>
									) : (
										<strong aria-current="page">{item.label}</strong>
									)}
								</React.Fragment>
							))}
						</nav>
					)}
					<div className="app-header__environment">
						<span className="status-dot" aria-hidden="true" />
						Live data
					</div>
				</header>

				<main className="app-main">
					<Routes>
						<Route path="/" element={<Navigate to="/home/index" replace />} />
						<Route path="/home/index" element={<Home />} />
						<Route path="/home/devices" element={<DevicesPage />} />
						<Route path="/devices/netatmo" element={<Netatmo />} />
						<Route path="/devices/voltaware" element={<Voltaware />} />
						<Route path="/devices/aton" element={<Aton />} />
						<Route path="/devices/bticino" element={<BTicino />} />
						<Route path="/devices/zucchetti" element={<Zucchetti />} />
						<Route path="/devices/smartplug" element={<Shelly />} />
						<Route path="/devices/washerdryer" element={<WasherDryer />} />
						<Route path="/devices/husqvarna" element={<Husqvarna />} />
						<Route path="/settings" element={<Settings />} />
						<Route path="/settings/ruleengine" element={<RuleEngine />} />
						<Route path="*" element={<Navigate to="/home/index" replace />} />
					</Routes>
				</main>
			</div>

			<nav className="mobile-nav" aria-label="Mobile navigation">
				<NavLink className={navClassName} to="/home/index"><FontAwesomeIcon icon={faHome} /><span>Home</span></NavLink>
				<NavLink className={navClassName} to="/home/devices"><FontAwesomeIcon icon={faCubes} /><span>Devices</span></NavLink>
				<NavLink className={navClassName} to="/settings/ruleengine"><FontAwesomeIcon icon={faLeaf} /><span>Rules</span></NavLink>
				<NavLink className={navClassName} to="/settings" end><FontAwesomeIcon icon={faCog} /><span>Settings</span></NavLink>
			</nav>
		</div>
	);
};

export default BasePage;
